import { Endeavour } from './Endeavour';
import { ServiceAPIFB } from './Protocol/EndeavourProtocol';

export type PipelineConfigurationCache = {
  eventsToBlockOrAllow: string[];
  revertBlockListToAllowList: boolean;
  lastUpdated: Date;
};

export class PipelineConfiguration {
  private constructor() {}
  private static instance: PipelineConfiguration;
  private eventsToBlockOrAllow: string[] = [];
  private revertBlockListToAllowList: boolean = true;
  private blockAll: boolean = true;
  public pipelineConfigCache: PipelineConfigurationCache = {
    eventsToBlockOrAllow: [],
    revertBlockListToAllowList: false,
    lastUpdated: new Date('2012-01-01'),
  };

  public static initialize(): PipelineConfiguration {
    PipelineConfiguration.instance = new PipelineConfiguration();
    /* tslint:disable-next-line:no-floating-promises */
    PipelineConfiguration.refreshConfiguration();

    return PipelineConfiguration.instance;
  }

  public static getInstance(): PipelineConfiguration {
    if (!PipelineConfiguration.instance) {
      PipelineConfiguration.instance = PipelineConfiguration.initialize();
    }
    return PipelineConfiguration.instance;
  }

  public static isEventAllowed(name: string): boolean {
    const pipelineConfiguration = PipelineConfiguration.getInstance();

    if (pipelineConfiguration.blockAll) {
      return false;
    }

    if (pipelineConfiguration.revertBlockListToAllowList) {
      // eventsToBlockOrAllow is an allow list
      return pipelineConfiguration.eventsToBlockOrAllow.includes(name);
    } else {
      // eventsToBlockOrAllow is a block list
      return !pipelineConfiguration.eventsToBlockOrAllow.includes(name);
    }
  }

  public static async refreshConfiguration(): Promise<PipelineConfiguration> {
    const pipelineConfiguration = PipelineConfiguration.getInstance();
    pipelineConfiguration.eventsToBlockOrAllow = [];

    if (this.validPipelineConfigurationCacheExists()) {
      this.updatePipelineConfigurationFromCache();
      return pipelineConfiguration;
    }

    try {
      const configurationReply: ServiceAPIFB.GetPipelineConfigurationReply = await Endeavour.getPipelineConfigurationReply();

      pipelineConfiguration.revertBlockListToAllowList = configurationReply.revertBlockedToAllowedEvents();

      for (let i = 0; i < configurationReply.blockedEventsLength(); i++) {
        pipelineConfiguration.eventsToBlockOrAllow.push(configurationReply.blockedEvents(i));
      }

      pipelineConfiguration.blockAll = false;
      this.updateCache();
      return pipelineConfiguration;
    } catch (err) {
      pipelineConfiguration.blockAll = true;
      return pipelineConfiguration;
    }
  }

  private static validPipelineConfigurationCacheExists(): boolean {
    const pipelineConfiguration: PipelineConfiguration = PipelineConfiguration.getInstance();
    try {
      if (typeof pipelineConfiguration.pipelineConfigCache !== 'undefined') {
        const pipelineConfigurationCache: PipelineConfigurationCache = pipelineConfiguration.pipelineConfigCache;

        if (typeof pipelineConfigurationCache !== 'object' || pipelineConfigurationCache === null) {
          return false;
        }

        let THIRTY_MINUTES = 30 * 60 * 1000; /* ms */

        if (new Date().valueOf() - new Date(pipelineConfigurationCache.lastUpdated).valueOf() < THIRTY_MINUTES) {
          return true;
        }
      }

      return false;
    } catch (err) {
      return false;
    }
  }

  // Update the cache
  private static updateCache(): void {
    const pipelineConfiguration = PipelineConfiguration.getInstance();

    let pipelineConfigurationCache: PipelineConfigurationCache = {
      eventsToBlockOrAllow: pipelineConfiguration.eventsToBlockOrAllow,
      revertBlockListToAllowList: pipelineConfiguration.revertBlockListToAllowList,
      lastUpdated: new Date(),
    };
    pipelineConfiguration.pipelineConfigCache = pipelineConfigurationCache;
  }

  // Retreive the cached configuration and use it to update the PipelineConfiguration
  private static updatePipelineConfigurationFromCache(): void {
    const pipelineConfiguration = PipelineConfiguration.getInstance();
    const pipelineConfigurationCache: PipelineConfigurationCache = pipelineConfiguration.pipelineConfigCache;

    pipelineConfiguration.eventsToBlockOrAllow = pipelineConfigurationCache.eventsToBlockOrAllow;
    pipelineConfiguration.revertBlockListToAllowList = pipelineConfigurationCache.revertBlockListToAllowList;
    pipelineConfiguration.blockAll = false;
  }
}
